import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { subscribeExerciseStates, broadcastExerciseState } from '../utils/realtime';

const ExerciseSyncContext = createContext(null);

export function ExerciseSyncProvider({ lessonId, studentId, role, seedStates, children }) {
  const lsKey = `ex_sync_${lessonId}_${studentId || role}`;

  const [states, setStates] = useState(() => {
    try { return JSON.parse(localStorage.getItem(lsKey) || '{}'); } catch { return {}; }
  });

  const broadcastTimers = useRef({});
  // Counts real-time broadcasts actually received from the other side, per
  // exercise, since this provider mounted (i.e. since the lesson was
  // opened) — as opposed to values merely hydrated from localStorage/seed
  // at startup, which can be stale leftovers from a much earlier session
  // with no live peer involved at all. This ref lives on the provider,
  // which — unlike the Board/Embed components that read otherState — never
  // unmounts when the user switches tabs, so it reliably distinguishes
  // "a peer is live right now" from "replaying old data on every remount".
  const liveUpdateCounts = useRef({});

  // Re-read localStorage whenever the identity key changes (e.g. teacher
  // navigates between students' homework, or the URL params finally settle
  // after mount). The useState lazy initializer only fires once, so without
  // this the provider would keep the empty snapshot it started with.
  useEffect(() => {
    try {
      const fresh = JSON.parse(localStorage.getItem(lsKey) || '{}');
      setStates(fresh);
    } catch {
      setStates({});
    }
  }, [lsKey]);

  // Hydrate from an explicit seed (typically the student's submitted
  // homework, fetched from Supabase after the provider has already
  // mounted). Rule: PRIOR STATE WINS over seed — mirrors the
  // localStorage merge at LessonWorkspace load and, crucially, prevents
  // a re-fetch of the server snapshot from clobbering in-progress local
  // edits when a student navigates away and comes back mid-homework.
  // The seed only fills in fields that prior state doesn't already
  // populate, so an initially empty teacher view still sees the
  // student's answers.
  useEffect(() => {
    if (!seedStates || typeof seedStates !== 'object') return;
    const entries = Object.entries(seedStates);
    if (entries.length === 0) return;
    setStates(prev => {
      const next = { ...prev };
      for (const [exId, exState] of entries) {
        if (!exState || typeof exState !== 'object') continue;
        next[exId] = {
          myState: { ...(exState.myState || {}), ...(prev[exId]?.myState || {}) },
          otherState: { ...(exState.otherState || {}), ...(prev[exId]?.otherState || {}) },
        };
      }
      try { localStorage.setItem(lsKey, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [seedStates, lsKey]);

  // Receive exercise state changes from the other side
  useEffect(() => {
    if (!lessonId) return;
    return subscribeExerciseStates(lessonId, ({ exerciseId, role: remoteRole, state }) => {
      if (remoteRole === role) return; // ignore own role echoes
      liveUpdateCounts.current[exerciseId] = (liveUpdateCounts.current[exerciseId] || 0) + 1;
      setStates(prev => {
        const next = { ...prev, [exerciseId]: { ...prev[exerciseId], otherState: state } };
        try { localStorage.setItem(lsKey, JSON.stringify(next)); } catch {}
        return next;
      });
    });
  }, [lessonId, role, lsKey]);

  const updateExerciseState = useCallback((exerciseId, partial) => {
    if (!lessonId || !exerciseId) return;
    setStates(prev => {
      const current = prev[exerciseId]?.myState || {};
      const next = { ...current, ...partial };
      const newStates = { ...prev, [exerciseId]: { ...prev[exerciseId], myState: next } };
      try { localStorage.setItem(lsKey, JSON.stringify(newStates)); } catch {}

      clearTimeout(broadcastTimers.current[exerciseId]);
      broadcastTimers.current[exerciseId] = setTimeout(() => {
        broadcastExerciseState(lessonId, exerciseId, role, next);
      }, 100);

      return newStates;
    });
  }, [lessonId, role, lsKey]);

  const getExerciseState = useCallback((exerciseId) => {
    return states[exerciseId] || { myState: {}, otherState: {} };
  }, [states]);

  const getLiveUpdateCount = useCallback((exerciseId) => {
    return liveUpdateCounts.current[exerciseId] || 0;
  }, []);

  return (
    <ExerciseSyncContext.Provider value={{ getExerciseState, updateExerciseState, getLiveUpdateCount, role, ready: !!lessonId }}>
      {children}
    </ExerciseSyncContext.Provider>
  );
}

export function useExerciseSync(exerciseId) {
  const ctx = useContext(ExerciseSyncContext);
  if (!ctx || !exerciseId) return { myState: {}, otherState: {}, updateState: () => {}, role: 'teacher', ready: false, liveUpdateCount: 0 };
  const { myState, otherState } = ctx.getExerciseState(exerciseId);
  return {
    myState: myState || {},
    otherState: otherState || {},
    updateState: (partial) => ctx.updateExerciseState(exerciseId, partial),
    role: ctx.role,
    ready: ctx.ready,
    // Number of genuine real-time broadcasts received for this exercise
    // since the provider mounted. Consumers should only apply otherState as
    // a live sync when this has increased since their own last check —
    // otherwise they'd be replaying stale localStorage-hydrated data on
    // every remount (e.g. every tab switch) instead of reacting to an
    // actual live peer update.
    liveUpdateCount: ctx.getLiveUpdateCount(exerciseId),
  };
}
